import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Chip, TextField, CircularProgress, Stack, Alert, Collapse, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { getSuperSentinelStatus, activateSuperSentinel, deactivateSuperSentinel } from '../../../services/device.service';
import { SuperSentinelStatus } from '../../../types/SuperSentinel';
import { DeviceEnriched } from '../../../types/DeviceEnriched';
import { OverviewCard } from '../../common/OverviewCard/OverviewCard';

interface Notification {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

interface SuperSentinelPanelProps {
  deviceInfo: DeviceEnriched;
  statusDeviceId: string;
  activateDeviceId: string;
  deactivateDeviceId: string;
  onDeactivated?: () => void;
}

const toLocalDateValue = (): string => new Date().toISOString().slice(0, 10);

const toTomorrowDateValue = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const timestampToDateValue = (ts: string): string => ts.slice(0, 10);

const toIsoWithMicros = (dateOnly: string): string => {
  const now = new Date();
  const [y, m, d] = dateOnly.split('-').map(Number);
  const combined = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds());
  return combined.toISOString().replace('Z', '000Z');
};

const deriveOverallStatus = (data: SuperSentinelStatus | null) => {
  if (!data) return null;
  if (data.activeDeviceIds.length > 0) return 'ACTIVE';
  if (data.errorDeviceIds.length > 0) return 'ERROR';
  if (data.unsupportedDeviceIds.length > 0) return 'UNSUPPORTED';
  if (data.inactiveDeviceIds.length > 0) return 'INACTIVE';
  return 'UNKNOWN';
};

const STATUS_CHIP_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  INACTIVE: 'warning',
  ERROR: 'error',
  UNSUPPORTED: 'default',
  UNKNOWN: 'default',
};

const SectionLabel = ({ text, infoText, rightNode }: { text: string; infoText?: string; rightNode?: React.ReactNode }) => (
  <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1.25, minHeight: 22 }}>
    <Stack direction='row' alignItems='center' spacing={0.5}>
      <Typography variant='overline' sx={{ fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1, color: 'text.secondary', pb: 0.25 }}>
        {text}
      </Typography>
      {infoText && (
        <Tooltip title={infoText} arrow placement='top'>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      )}
    </Stack>
    {rightNode}
  </Stack>
);

const resolveEnableUntil = (deviceInfo: DeviceEnriched): string =>
  deviceInfo.plannedSupersentinelDeactivationTimestamp
    ? timestampToDateValue(deviceInfo.plannedSupersentinelDeactivationTimestamp)
    : toTomorrowDateValue();

export const SuperSentinelPanel = ({ deviceInfo, statusDeviceId, activateDeviceId, deactivateDeviceId, onDeactivated }: SuperSentinelPanelProps) => {
  const [statusData, setStatusData] = useState<SuperSentinelStatus | null>(null);
  // Pre-load from deviceInfo; fall back to today if the field is absent.
  const [enableUntil, setEnableUntil] = useState<string>(() => resolveEnableUntil(deviceInfo));
  const [notification, setNotification] = useState<Notification | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingActivate, setLoadingActivate] = useState(false);
  const [loadingDeactivate, setLoadingDeactivate] = useState(false);

  useEffect(() => {
    setEnableUntil(resolveEnableUntil(deviceInfo));
  }, [deviceInfo.plannedSupersentinelDeactivationTimestamp]);

  const notify = (type: Notification['type'], message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleGetStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusData(null);
    try {
      const data = await getSuperSentinelStatus(statusDeviceId);
      setStatusData(data);
    } catch (err: any) {
      const isCancelled =
        err?.message?.toLowerCase().includes('cancel') || err?.message?.toLowerCase().includes('cancelled') || err?.code === 'ERR_CANCELED';
      if (!isCancelled) {
        notify('error', `Failed to fetch status: ${err?.message ?? 'Unknown error'}`);
      }
    } finally {
      setLoadingStatus(false);
    }
  }, [statusDeviceId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleGetStatus();
    }, 300);
    return () => clearTimeout(timer);
  }, [handleGetStatus]);

  const handleActivate = async () => {
    if (!enableUntil) {
      notify('warning', 'Please set an "Enable Until" date and time.');
      return;
    }
    setLoadingActivate(true);
    try {
      await activateSuperSentinel(activateDeviceId, { enableUntil: toIsoWithMicros(enableUntil) });
      notify('success', 'SuperSentinel activated successfully.');
      handleGetStatus();
    } catch (err: any) {
      notify('error', 'Unable to activate Super Sentinel. Please check the device firmware is up to date, or try again later.');
    } finally {
      setLoadingActivate(false);
    }
  };

  const handleDeactivate = async () => {
    setLoadingDeactivate(true);
    try {
      await deactivateSuperSentinel(deactivateDeviceId);
      notify('success', 'SuperSentinel deactivated successfully.');
      setEnableUntil(toTomorrowDateValue()); // ← immediately reset to default while refetch is in-flight
      onDeactivated?.(); // ← async: when deviceInfo updates with null, useEffect confirms it
      handleGetStatus();
    } catch (err: any) {
      const isAlreadyInactive = err?.status === 500 || err?.statusCode === 500;
      if (isAlreadyInactive) {
        notify('error', 'Deactivation failed — device may already be inactive.');
      } else {
        notify('error', `Deactivation failed: ${err?.message ?? 'Unknown error'}`);
      }
    } finally {
      setLoadingDeactivate(false);
    }
  };

  const overallStatus = deriveOverallStatus(statusData);
  const isActive = overallStatus === 'ACTIVE';
  const isUnsupported = overallStatus === 'UNSUPPORTED';
  const isInactive = overallStatus === 'INACTIVE';

  const sectionSx = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    px: 1.5,
    py: 1,
    backgroundColor: 'grey.50',
    border: '2px solid',
    borderColor: '#dfe6ef',
    borderRadius: 4,
  };

  return (
    <Box sx={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}>
      <OverviewCard title='Super Sentinel Activation'>
        <Collapse in={!!notification}>
          {notification && (
            <Alert severity={notification.type} onClose={() => setNotification(null)} sx={{ py: 0 }}>
              {notification.message}
            </Alert>
          )}
        </Collapse>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            width: 'fit-content',
            gap: 1,
            p: 1.25,
            backgroundColor: 'transparent',
          }}
        >
          {/* ── Status ── */}
          <Box sx={{ ...sectionSx, minWidth: 92 }}>
            <SectionLabel
              text='Status'
              rightNode={
                <IconButton size='small' onClick={handleGetStatus} disabled={loadingStatus} sx={{ p: 0.25 }}>
                  {loadingStatus ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              }
            />
            {overallStatus && (
              <Chip
                label={overallStatus}
                size='small'
                color={STATUS_CHIP_COLOR[overallStatus]}
                variant='outlined'
                sx={{
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  mt: 0.5,
                  width: 'fit-content',
                  borderRadius: 5,
                  ...(overallStatus === 'INACTIVE' ? { backgroundColor: '#FFF2E0' } : {}),
                }}
              />
            )}
          </Box>

          {/* ── Activate ── */}
          <Box sx={{ ...sectionSx, minWidth: { xs: 260, md: 340 } }}>
            <SectionLabel
              text='Activate'
              infoText='Defaults to tomorrow, so activating without changing the date keeps Super Sentinel on for the next 24 hours.'
            />
            <Stack direction='row' alignItems='center' gap={1}>
              <TextField
                label='Enable Until'
                type='date'
                size='small'
                fullWidth
                value={enableUntil}
                onChange={(e) => setEnableUntil(e.target.value)}
                inputProps={{ min: toLocalDateValue() }}
                InputLabelProps={{ shrink: true }}
                disabled={isActive || isUnsupported}
                sx={{ '& .MuiInputBase-root': { backgroundColor: 'background.paper' } }}
              />
              <Button
                variant='contained'
                color='success'
                size='small'
                startIcon={loadingActivate ? <CircularProgress size={14} color='inherit' /> : <PowerSettingsNewIcon fontSize='small' />}
                onClick={handleActivate}
                disabled={loadingActivate || isActive || isUnsupported || loadingStatus}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, borderRadius: '15px', px: 2.25, fontWeight: 700 }}
              >
                {loadingActivate ? 'Activating…' : isActive ? 'Already Active' : isUnsupported ? 'Not Supported' : 'Activate'}
              </Button>
            </Stack>
          </Box>

          {/* ── Deactivate ── */}
          <Box sx={{ ...sectionSx, minWidth: 140 }}>
            <SectionLabel text='Deactivate' />
            <Button
              variant='outlined'
              color='error'
              size='small'
              startIcon={loadingDeactivate ? <CircularProgress size={14} color='inherit' /> : <PowerOffIcon fontSize='small' />}
              onClick={handleDeactivate}
              disabled={loadingDeactivate || isUnsupported || isInactive}
              sx={{ borderRadius: '15px', px: 2.25, fontWeight: 700 }}
            >
              {loadingDeactivate ? 'Deactivating…' : 'Deactivate'}
            </Button>
          </Box>
        </Box>
      </OverviewCard>
    </Box>
  );
};
